import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

const AccountDeletedPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    // Get user email from location state (passed during deletion)
    if (location.state?.email) {
      setUserEmail(location.state.email);
    }
  }, [location.state]);

  const handleSubmitFeedback = async () => {
    if (!feedback.trim()) {
      setError('Please provide your feedback before submitting');
      return;
    }

    if (feedback.length > 1000) {
      setError('Feedback must be 1000 characters or less');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/';
      const response = await fetch(`${backendUrl}api/exit-feedback/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: userEmail,
          reason: feedback
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit feedback. You can still return to the landing page.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLanding = () => {
    navigate('/login');
  };

  return (
    <>
    <SEO title="Account Deleted - Couplendar" />
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: '#F9F7F4',
      display: 'flex',
      alignItems: 'center',
      py: 4
    }}>
      <Container maxWidth="md">
        <Paper sx={{
          p: { xs: 3, md: 5 },
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}>
          {!submitted ? (
            <>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: '#333',
                  textAlign: 'center',
                  mb: 2
                }}
              >
                We're Sorry to See You Go
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: '#666',
                  textAlign: 'center',
                  mb: 4
                }}
              >
                Your account has been successfully deleted. All your data has been permanently removed from our system.
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 500,
                    color: '#333',
                    mb: 2
                  }}
                >
                  We'd Love Your Feedback
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    mb: 2
                  }}
                >
                  Would you mind sharing why you decided to delete your account? Your feedback helps us improve our service for other couples.
                </Typography>

                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  placeholder="Tell us what we could have done better..."
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  disabled={loading}
                  sx={{ mb: 2 }}
                />

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Box sx={{
                  display: 'flex',
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' }
                }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSubmitFeedback}
                    disabled={loading || !feedback.trim()}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      backgroundColor: '#C4856A',
                      boxShadow: '0 4px 12px rgba(196, 133, 106, 0.3)',
                      '&:hover': {
                        backgroundColor: '#B0745A',
                        boxShadow: '0 6px 16px rgba(196, 133, 106, 0.4)'
                      },
                      '&:disabled': {
                        background: '#E0E0E0'
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Submit Feedback'}
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleGoToLanding}
                    disabled={loading}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      borderColor: '#7B6B8D',
                      color: '#7B6B8D',
                      '&:hover': {
                        borderColor: '#6A5A7A',
                        backgroundColor: 'rgba(123, 107, 141, 0.04)'
                      }
                    }}
                  >
                    Skip & Go to Landing Page
                  </Button>
                </Box>
              </Box>
            </>
          ) : (
            <>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: '#333',
                  textAlign: 'center',
                  mb: 2
                }}
              >
                Thank You for Your Feedback
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: '#666',
                  textAlign: 'center',
                  mb: 4
                }}
              >
                We appreciate you taking the time to share your thoughts. Your feedback will help us improve Couplendar for future users.
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleGoToLanding}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    px: 4,
                    backgroundColor: '#C4856A',
                    boxShadow: '0 4px 12px rgba(196, 133, 106, 0.3)',
                    '&:hover': {
                      backgroundColor: '#B0745A',
                      boxShadow: '0 6px 16px rgba(196, 133, 106, 0.4)'
                    }
                  }}
                >
                  Go to Landing Page
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </Box>
    </>
  );
};

export default AccountDeletedPage;
